import { useState, useEffect } from 'react'
import {
  Settings,
  Download,
  ExternalLink,
  Loader2,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Rocket,
  Sparkles,
  RefreshCcw
} from 'lucide-react'
import WidgetConfig from '../../components/widget/WidgetConfig'
import IntegrationGuide from '../../components/widget/IntegrationGuide'
import EmbeddableChatWidget from '../../components/widget/EmbeddableChatWidget'
import {
  getBotConfiguration,
  saveBotConfiguration,
  mapApiResponseToConfig,
  mapConfigToApiData
} from '../../services/bot_configuration'
import authService from '../../services/auth'
import subscriptionService from '../../services/subscriptionService'
import paymentService from '../../services/paymentService'
import AlertModal from '../../components/popups/confirmationModal/AlertModal'

const WidgetManager = () => {
  const [activeTab, setActiveTab] = useState('config')
  const [currentWidget, setCurrentWidget] = useState(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [originalWidget, setOriginalWidget] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false)
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(true)
  const [isStartingPlan, setIsStartingPlan] = useState(false)
  const [subscriptionStatus, setSubscriptionStatus] = useState(null) // { isExpired: boolean, message: string }
  const [hasModel, setHasModel] = useState(true) // Track if model exists
  const [captchaAnswer, setCaptchaAnswer] = useState('')
  const [captchaQuestion, setCaptchaQuestion] = useState({ num1: 0, num2: 0, answer: 0 })
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  })

  // Generate a new math captcha question
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1
    const num2 = Math.floor(Math.random() * 10) + 1
    setCaptchaQuestion({ num1, num2, answer: num1 + num2 })
    setCaptchaAnswer('')
  }

  // Initialize captcha on mount
  useEffect(() => {
    generateCaptcha()
  }, [])

  // Default widget configuration
  const defaultConfig = {
    id: 'default',
    name: 'Default Widget',
    userId: null,
    botUuid: null,
    botSecretKey: null,
    botId: 'demo-bot-123',
    botToken: 'demo-bot-token-123',
    position: 'bottom-right',
    theme: 'dark',
    themeStyle: 'solid',
    primaryColor: '#3b82f6',
    welcomeMessage: 'Hello! How can I help you today?',
    placeholder: 'Type your message...',
    showWelcomeMessage: true,
    autoOpen: false,
    zIndex: 9999,
    customCSS: '',
    enableAnalytics: true,
    googleAnalytics: false,
    // UserModel fields
    llmProvider: 'google',
    modelName: 'gemini-2.5-flash-lite',
    apiKeyEncrypted: '',
    botName: 'Talkida Technologies',
    greetingMsg: 'Hello! How can I help you today?',
    byeMsg: 'Bye, have a great day!',
    notFoundInfo: 'I\'m sorry, I don\'t have that information at the moment.',
    customSystPrompt: '',
    maxTokens: 150,
    temperature: 0.0,
    botAim: '',
    companyName: 'Talkida.co.in',
    // New fields
    chatbotAvatar: '🤖',
    customAvatar: '',
    uploadedDocument: null,
    enableLeadCollection: false,
    leadFields: 'full_name,email_address,phone_number',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }

  useEffect(() => {
    checkSubscriptionAndLoad()
  }, [])

  const checkSubscriptionAndLoad = async () => {
    try {
      setIsCheckingSubscription(true)
      setError(null)

      // Check subscription first
      const subscriptionResponse = await subscriptionService.checkSubscription()

      // Handle both response formats:
      // Active: { error: false, msg: "Active subscription found", data: {...} }
      // Expired: { detail: { error: true, msg: "Subscription expired", data: null } }
      // No subscription: { detail: { error: true, msg: "No subscription found", data: null } }

      if (subscriptionResponse.detail && subscriptionResponse.detail.error) {
        // Subscription expired or no subscription
        const isExpired = subscriptionResponse.detail.msg?.toLowerCase().includes('expired')
        setSubscriptionStatus({
          isExpired,
          message: subscriptionResponse.detail.msg || 'No active subscription'
        })
        setHasActiveSubscription(false)
        setIsLoading(false)
      } else if (!subscriptionResponse.error && subscriptionResponse.data && subscriptionResponse.data.is_subscribed) {
        // Active subscription
        setSubscriptionStatus(null)
        setHasActiveSubscription(true)
        // Load bot configuration only if subscription is active
        await loadBotConfiguration()
      } else {
        // No subscription found
        setSubscriptionStatus({
          isExpired: false,
          message: subscriptionResponse.msg || 'No subscription found'
        })
        setHasActiveSubscription(false)
        setIsLoading(false)
      }
    } catch (error) {
      console.error('Error checking subscription:', error)

      // Handle error response format
      if (error.response?.data?.detail?.error) {
        const isExpired = error.response.data.detail.msg?.toLowerCase().includes('expired')
        setSubscriptionStatus({
          isExpired,
          message: error.response.data.detail.msg || 'Subscription error'
        })
      } else {
        setSubscriptionStatus({
          isExpired: false,
          message: 'Unable to check subscription status'
        })
      }

      setHasActiveSubscription(false)
      setIsLoading(false)
    } finally {
      setIsCheckingSubscription(false)
    }
  }

  const handleStartFreePlan = async () => {
    try {
      // Verify captcha first
      const userAnswer = parseInt(captchaAnswer.trim())
      if (isNaN(userAnswer) || userAnswer !== captchaQuestion.answer) {
        setAlertModal({
          isOpen: true,
          title: 'Verification Failed',
          message: 'Please solve the math problem correctly to continue.',
          type: 'warning'
        })
        generateCaptcha() // Generate new question
        return
      }

      setIsStartingPlan(true)
      setError(null)

      // Generate a simple token based on the captcha answer for backend verification
      const captchaToken = `captcha_${captchaQuestion.answer}_${Date.now()}`
      const response = await paymentService.startFreePlan(captchaToken)

      // Handle both response formats:
      // Success: { error: false, msg: "...", data: {...} }
      // Error: { detail: { error: true, msg: "...", data: null } }
      const isSuccess =
        (!response.error && response.data) ||
        (response.detail && !response.detail.error)

      if (isSuccess) {
        setAlertModal({
          isOpen: true,
          title: 'Success',
          message: response.msg || response.detail?.msg || 'Free plan activated successfully! You can now access the Widget Manager.',
          type: 'success'
        })
        // Refresh subscription status and load config
        await checkSubscriptionAndLoad()
        // Reset captcha after successful activation
        generateCaptcha()
      } else {
        const errorMsg = response.detail?.msg || response.msg || 'Failed to start free plan. Please try again.'
        setAlertModal({
          isOpen: true,
          title: 'Error',
          message: errorMsg,
          type: 'danger'
        })
        // Reset captcha on error
        generateCaptcha()
      }
    } catch (error) {
      console.error('Error starting free plan:', error)
      const errorMsg = error.response?.data?.detail?.msg || error.response?.data?.msg || error.message || 'Failed to start free plan. Please try again.'
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: errorMsg,
        type: 'danger'
      })
      // Reset captcha on error
      generateCaptcha()
    } finally {
      setIsStartingPlan(false)
    }
  }

  const loadBotConfiguration = async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Check if user is authenticated
      if (!authService.isAuthenticated()) {
        throw new Error('User not authenticated')
      }

      // Try to load from API first
      const apiData = await getBotConfiguration()

      // Check if response indicates "No model found"
      if (apiData.detail && apiData.detail.error && apiData.detail.msg === 'No model found') {
        setHasModel(false)
        // Set default config for new model
        setCurrentWidget(defaultConfig)
        setOriginalWidget(JSON.parse(JSON.stringify(defaultConfig)))
        // Set active tab to bot-config
        setActiveTab('config')
        setIsLoading(false)
        return
      }

      // Model exists, proceed normally
      setHasModel(true)
      const config = mapApiResponseToConfig(apiData.data)

      setCurrentWidget(config)
      setOriginalWidget(JSON.parse(JSON.stringify(config))) // Deep copy

      // Also save to localStorage as backup
      localStorage.setItem('talkida_widget', JSON.stringify(config))

    } catch (apiError) {
      console.warn('API not available, loading from localStorage:', apiError)

      // Handle authentication errors
      if (apiError.message === 'User not authenticated' || apiError.response?.status === 401) {
        setError('Please log in to access bot configuration.')
        // Redirect to login after a delay
        setTimeout(() => {
          window.location.href = '/signin'
        }, 2000)
        return
      }

      // Check if error response indicates "No model found"
      if (apiError.response?.data?.detail?.error && apiError.response.data.detail.msg === 'No model found') {
        setHasModel(false)
        setCurrentWidget(defaultConfig)
        setOriginalWidget(JSON.parse(JSON.stringify(defaultConfig)))
        setActiveTab('config')
        setIsLoading(false)
        return
      }

      // Fallback to localStorage if API fails
      const savedWidget = localStorage.getItem('talkida_widget')
      if (savedWidget) {
        try {
          const parsed = JSON.parse(savedWidget)
          setCurrentWidget(parsed)
          setOriginalWidget(JSON.parse(JSON.stringify(parsed))) // Deep copy
          setHasModel(true) // Assume model exists if we have saved data
        } catch (error) {
          console.error('Error loading widget from localStorage:', error)
          setCurrentWidget(defaultConfig)
          setOriginalWidget(JSON.parse(JSON.stringify(defaultConfig)))
          setHasModel(false)
        }
      } else {
        setCurrentWidget(defaultConfig)
        setOriginalWidget(JSON.parse(JSON.stringify(defaultConfig)))
        setHasModel(false)
      }

      // Set error only if it's a real API error (not just no data)
      if (apiError.response?.status !== 404) {
        setError('Failed to load configuration from server. Using local configuration.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const saveWidget = (widget) => {
    const updatedWidget = {
      ...widget,
      updatedAt: new Date().toISOString()
    }

    setCurrentWidget(updatedWidget)
    setOriginalWidget(JSON.parse(JSON.stringify(updatedWidget))) // Update original
    setHasUnsavedChanges(false)
    localStorage.setItem('talkida_widget', JSON.stringify(updatedWidget))
  }

  const handleConfigChange = (widget) => {
    setCurrentWidget(widget)

    // Check if there are changes compared to original
    const hasChanges = JSON.stringify(widget) !== JSON.stringify(originalWidget)
    setHasUnsavedChanges(hasChanges)
  }

  const saveToAPI = async () => {
    try {
      setIsSaving(true)
      setError(null)
      setSuccessMessage(null)

      // Check authentication before saving
      if (!authService.isAuthenticated()) {
        setError('Please log in to save bot configuration.')
        setTimeout(() => {
          window.location.href = '/signin'
        }, 2000)
        return
      }

      // Single POST endpoint handles both create and update
      try {
        await saveBotConfiguration(currentWidget)
        setSuccessMessage('Bot configuration saved successfully!')
      } catch (saveError) {
        if (saveError.response?.status === 401) {
          setError('Authentication expired. Please log in again.')
          setTimeout(() => {
            window.location.href = '/signin'
          }, 2000)
          return
        } else {
          throw saveError
        }
      }

      // Update local state
      const updatedWidget = {
        ...currentWidget,
        updatedAt: new Date().toISOString()
      }

      setCurrentWidget(updatedWidget)
      setOriginalWidget(JSON.parse(JSON.stringify(updatedWidget)))
      setHasUnsavedChanges(false)

      // Save to localStorage as backup
      localStorage.setItem('talkida_widget', JSON.stringify(updatedWidget))

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000)

    } catch (error) {
      console.error('Error saving widget:', error)

      // Handle specific error types
      if (error.response?.status === 401) {
        setError('Authentication expired. Please log in again.')
        setTimeout(() => {
          window.location.href = '/signin'
        }, 2000)
      } else {
        setError('Error saving bot configuration. Please try again.')
        // Clear error message after 5 seconds
        setTimeout(() => setError(null), 5000)
      }
    } finally {
      setIsSaving(false)
    }
  }

  const resetWidget = () => {
    setCurrentWidget(defaultConfig)
    setOriginalWidget(JSON.parse(JSON.stringify(defaultConfig)))
    setHasUnsavedChanges(false)
    localStorage.setItem('talkida_widget', JSON.stringify(defaultConfig))
  }

  const exportWidget = (widget) => {
    const exportData = {
      ...widget,
      exportedAt: new Date().toISOString(),
      version: '1.0.0'
    }

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `talkida-widget-${widget.name.toLowerCase().replace(/\s+/g, '-')}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const generateEmbedCode = (widget) => {
    return `<!-- Talkida Chat Widget -->
<script 
  src="https://cdn.talkida.co.in/talkida-widget.js" 
  bot-id="${widget.botId}"
  bot-token="${widget.botToken}"
  data-position="${widget.position}"
  data-theme="${widget.theme}"
  data-primary-color="${widget.primaryColor}"
  data-theme-style="${widget.themeStyle}"
  data-welcome-message="${widget.welcomeMessage}"
  data-placeholder="${widget.placeholder}"
  data-show-welcome="${widget.showWelcomeMessage}"
  data-auto-open="${widget.autoOpen}"
  data-z-index="${widget.zIndex}"
  defer 
  type="text/javascript">
</script>`
  }

  const tabs = [
    { id: 'config', label: 'Configuration', icon: Settings },
    { id: 'integrate', label: 'Integration', icon: ExternalLink }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Widget Manager</h1>
          <p className="text-secondary-400">
            Configure and manage your embeddable chat widget for your website
          </p>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto">
          {/* Checking Subscription State */}
          {isCheckingSubscription && (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center space-x-3 text-secondary-400">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span>Checking subscription status...</span>
              </div>
            </div>
          )}

          {/* No Active Subscription - Show Start Free Plan Button */}
          {!isCheckingSubscription && !hasActiveSubscription && (
            <div className="flex items-center justify-center py-16">
              <div className="bg-secondary-800 rounded-2xl p-12 max-w-2xl w-full text-center border border-secondary-700">
                <div className="mb-6 flex justify-center">
                  <div className={`p-4 rounded-full ${subscriptionStatus?.isExpired
                    ? 'bg-red-500/20'
                    : 'bg-primary-500/20'
                    }`}>
                    {subscriptionStatus?.isExpired ? (
                      <AlertCircle className="w-16 h-16 text-red-400" />
                    ) : (
                      <Rocket className="w-16 h-16 text-primary-400" />
                    )}
                  </div>
                </div>
                <h2 className="text-3xl font-bold text-white mb-4">
                  {subscriptionStatus?.isExpired
                    ? 'Subscription Expired'
                    : 'Start Your Free Plan'}
                </h2>
                <p className="text-lg text-white/80 mb-8 leading-relaxed">
                  {subscriptionStatus?.isExpired
                    ? subscriptionStatus.message || 'Your subscription has expired. Please renew your subscription to continue using the Widget Manager.'
                    : subscriptionStatus?.message || 'You need an active subscription to access the Widget Manager. Start with our free plan to get started with your AI chatbot widget.'}
                </p>
                {!subscriptionStatus?.isExpired && (
                  <div className="space-y-4">
                    {/* Math Captcha */}
                    <div className="bg-secondary-700/50 border border-secondary-600 rounded-lg p-6">
                      <div className="flex flex-col items-center space-y-4">
                        <div className="flex items-center space-x-2">
                          <label className="text-sm font-medium text-white">
                            Security Verification:
                          </label>
                          <button
                            onClick={generateCaptcha}
                            className="text-primary-400 hover:text-primary-300 transition-colors p-1"
                            title="Generate new question"
                          >
                            <RefreshCcw className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="bg-secondary-800 px-6 py-4 rounded-lg border border-secondary-600">
                            <span className="text-2xl font-bold text-white">
                              {captchaQuestion.num1} + {captchaQuestion.num2} = ?
                            </span>
                          </div>
                          <input
                            type="number"
                            value={captchaAnswer}
                            onChange={(e) => setCaptchaAnswer(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && captchaAnswer.trim() && !isStartingPlan) {
                                handleStartFreePlan()
                              }
                            }}
                            placeholder="Answer"
                            className="w-24 px-4 py-3 bg-secondary-800 border border-secondary-600 rounded-lg text-white text-center text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            autoComplete="off"
                          />
                        </div>
                        <p className="text-xs text-secondary-400 text-center">
                          Please solve the math problem to verify you're human
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleStartFreePlan}
                      disabled={isStartingPlan || !captchaAnswer.trim()}
                      className="inline-flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-primary-500 to-purple-500 hover:from-primary-600 hover:to-purple-600 disabled:from-primary-500/50 disabled:to-purple-500/50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none"
                    >
                      {isStartingPlan ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Activating Free Plan...</span>
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-5 h-5" />
                          <span>Start Your Free Plan</span>
                        </>
                      )}
                    </button>
                    {!captchaAnswer.trim() && (
                      <p className="text-xs text-secondary-400 mt-2 text-center">
                        Please solve the math problem above to continue
                      </p>
                    )}
                  </div>
                )}
                {subscriptionStatus?.isExpired && (
                  <div className="mt-6 bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                    <div className="flex items-center justify-center space-x-3">
                      <AlertCircle className="w-5 h-5 text-red-400" />
                      <span className="text-red-400 text-sm">{subscriptionStatus.message}</span>
                    </div>
                  </div>
                )}
                {error && (
                  <div className="mt-6 bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <AlertCircle className="w-5 h-5 text-red-400" />
                      <span className="text-red-400 text-sm">{error}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Loading State */}
          {hasActiveSubscription && isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center space-x-3 text-secondary-400">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span>Loading bot configuration...</span>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-500/10 border border-red-500/20 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <span className="text-red-400">{error}</span>
              </div>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 bg-green-500/10 border border-green-500/20 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-green-400">{successMessage}</span>
              </div>
            </div>
          )}

          {hasActiveSubscription && currentWidget && !isLoading && (
            <div className="space-y-6">
              {/* Widget Header */}
              <div className="bg-secondary-800 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-white">{currentWidget.name}</h2>
                    <p className="text-secondary-400">
                      Created {new Date(currentWidget.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={loadBotConfiguration}
                      disabled={isLoading}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-500/50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                    >
                      <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                      <span>Refresh</span>
                    </button>
                    <button
                      onClick={resetWidget}
                      className="flex items-center space-x-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                      <span>Reset</span>
                    </button>
                    <button
                      onClick={() => exportWidget(currentWidget)}
                      className="flex items-center space-x-2 px-4 py-2 bg-secondary-700 text-secondary-300 hover:bg-secondary-600 rounded-lg transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      <span>Export</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Tab Navigation */}
              <div className="flex space-x-1 bg-secondary-800 p-1 rounded-lg">
                {tabs.map((tab) => {
                  const Icon = tab.icon
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === tab.id
                        ? 'bg-primary-500 text-white'
                        : 'text-secondary-300 hover:text-white hover:bg-secondary-700'
                        }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{tab.label}</span>
                    </button>
                  )
                })}
              </div>

              {/* Tab Content */}
              <div className="bg-secondary-800 rounded-lg p-6">
                {activeTab === 'config' && (
                  <>
                    <WidgetConfig
                      config={currentWidget}
                      onConfigChange={handleConfigChange}
                      hasModel={hasModel}
                    />

                    {/* Save Button */}
                    {hasUnsavedChanges && (
                      <div className="mt-6 pt-6 border-t border-secondary-700">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2 text-yellow-400">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm font-medium">You have unsaved changes</span>
                          </div>
                          <div className="flex space-x-3">
                            <button
                              onClick={resetWidget}
                              className="px-4 py-2 text-sm font-medium text-secondary-300 hover:text-white transition-colors"
                            >
                              Reset
                            </button>
                            <button
                              onClick={saveToAPI}
                              disabled={isSaving}
                              className="px-6 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center space-x-2"
                            >
                              {isSaving ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  <span>Saving...</span>
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                                  </svg>
                                  <span>Save Changes</span>
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {activeTab === 'integrate' && (
                  <IntegrationGuide widget={currentWidget} />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Real Widget - Positioned according to configuration */}
        {hasActiveSubscription && currentWidget && (
          <EmbeddableChatWidget
            botId={currentWidget.botUuid}
            botToken={currentWidget.botSecretKey}
            chatbotAvatar={currentWidget.chatbotAvatar}
            customAvatar={currentWidget.customAvatar}
            botName={currentWidget.botName}
            position={currentWidget.position}
            theme={currentWidget.theme}
            themeStyle={currentWidget.themeStyle}
            primaryColor={currentWidget.primaryColor}
            placeholder={currentWidget.placeholder}
            zIndex={currentWidget.zIndex}
            autoOpen={currentWidget.autoOpen}
            showWelcomeMessage={currentWidget.showWelcomeMessage}
            welcomeMessage={currentWidget.greetingMsg || currentWidget.welcomeMessage}
          />
        )}
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />
    </div>
  )
}

export default WidgetManager
